import React, { Component, FC, ReactElement } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { Alert } from '@zooplus/ui-components';

import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import { getSentryService } from '@services/sentryService';

export type SentryErrorBoundaryProps = {
  fallbackRender?: (
    props: FallbackProps,
  ) => ReactElement<unknown, string | FC | typeof Component> | null;
  featureName?: string;
};

/**
 * ErrorBoundary which will send to Sentry all the uncaptured exceptions raised from the given children.
 */
const SentryErrorBoundary: React.FC<SentryErrorBoundaryProps> = ({
  children,
  fallbackRender,
  featureName,
}) => {
  const { t } = useTranslation();
  const { config } = useConfig();
  const sentry = getSentryService(config.env, config);

  const handleError = (error: Error) => {
    // 1- Simple approach : capture error with no tag
    // ------------------------
    sentry.captureException(error, (scope) => scope.setTags({ featureName }));
    // ------------------------
    // 2- Approach adding a tag
    // ------------------------
    // captureException(error, {
    //   // If you need to send things specific to this error on top of the error
    //   // itself then use this captureContext
    //   captureContext: (scope) => scope.setTags({ type: 'errorBoundary' }),
    // });
    // ------------------------

    sentry.captureException(error, (scope) => scope.setTags({ featureName }));
  };

  const fallbackRenderNullSafe =
    fallbackRender ??
    (() => {
      return (
        <Alert variant="warning">{t('checkout.payment.creditcard.error.method.unavailable')}</Alert>
      );
    });

  return (
    <ErrorBoundary fallbackRender={fallbackRenderNullSafe} onError={handleError}>
      {children}
    </ErrorBoundary>
  );
};

export default SentryErrorBoundary;
